import React, { useState, useEffect, ReactNode } from 'react';
import jwtDecode from 'jwt-decode';
import { persistor } from 'careand-redux/store';
import LoadingScreen from 'components/Utils/LoadingScreen';
import { useAppDispatch, getUnwrappedDispatch } from '../../careand-redux/useAppDispatch';
import { api } from 'careand-redux/api/ApiThunks';
import { authActions } from 'careand-redux/slices/auth';
import { isEmbeddedFrame } from 'services/frameScope';

export const ENTER_AS_READY = 'careand:enterAs:ready';
export const ENTER_AS_TOKEN = 'careand:enterAs:token';

export interface EnterAsTokenMessage {
  type: typeof ENTER_AS_TOKEN;
  token: string;
  permissions: string[];
  role: 'physician' | 'admin';
}

/**
 * Boots a manager "enter tenant" debug session inside an embedded iframe.
 * The token is handed off from the parent window via postMessage — it never
 * appears in a URL and there is no public exchange endpoint.
 */
export default function EnterAsGate({ children }: { children: ReactNode }) {
  const _dispatch = useAppDispatch();
  const [ready, setReady] = useState(
    () => !(isEmbeddedFrame && new URLSearchParams(window.location.search).has('enterAs')),
  );

  useEffect(() => {
    if (ready) return;
    const dispatch = getUnwrappedDispatch(_dispatch);

    // Debug sessions are ephemeral — never persist them to IndexedDB.
    persistor.pause();
    window.history.replaceState({}, '', window.location.pathname);

    let done = false;

    async function login(message: EnterAsTokenMessage) {
      const { token, permissions, role } = message;
      const sub = jwtDecode<{ sub: string }>(token).sub;
      dispatch(authActions.setToken(token));
      const user =
        role === 'admin'
          ? await dispatch(api.Admins.getAdmin({ id: sub }))
          : await dispatch(api.Physicians.getPhysician({ id: sub }));
      dispatch(authActions.manualLogin({ token, user, permissions, role }));
    }

    function onMessage(event: MessageEvent) {
      if (done) return;
      if (event.origin !== window.location.origin) return;
      if (event.source !== window.parent) return;
      if (event.data?.type !== ENTER_AS_TOKEN) return;
      done = true;
      login(event.data as EnterAsTokenMessage)
        .catch((e: unknown) => console.error('EnterAs login failed', e))
        .finally(() => setReady(true));
    }

    window.addEventListener('message', onMessage);
    // Announce readiness until the parent hands over the token — the parent
    // attaches its listener before rendering the iframe, but re-announcing
    // covers reloads and slow mounts.
    const announce = window.setInterval(() => {
      if (done) return window.clearInterval(announce);
      window.parent.postMessage({ type: ENTER_AS_READY }, window.location.origin);
    }, 250);

    return () => {
      window.removeEventListener('message', onMessage);
      window.clearInterval(announce);
    };
  }, []);

  if (!ready) return <LoadingScreen />;
  return <>{children}</>;
}
